import { useMutation, useQuery, useQueryClient } from 'react-query'
import cloneDeep from 'lodash.clonedeep'
import { useCallback } from 'react'

import { useAuthentication } from '@hooks/useAuthentication'
import { BookEnum } from '@interfaces/BookEnum'
import { useModal } from '@hooks/contexts/ModalContext/ModalContext'
import { RecommendCatagoryType } from '@models/category/RecommendCatagoryType'
import { LikeSuccessStatusEnum } from '@models/book/LikeSuccessStatusEnum'
import { useBookAction } from '@hooks/book/useBookAction'
import { useCategoryAction } from './useCategoryAction'

export function useGetRecommendationBooks({
  categoryId,
  bookId,
  bookType,
  isEbook,
  limitPerPage = 20,
}: {
  categoryId?: number
  bookId?: number
  bookType?: BookEnum
  isEbook?: boolean
  limitPerPage?: number
}) {
  const categoryClient = useCategoryAction()
  const { isAuthenticated, user, canFetchApi } = useAuthentication()
  const loginModal = useModal({ modal: 'login' })
  const queryClient = useQueryClient()
  const bookClient = useBookAction()

  const { data: recommends, isLoading: isRecommendLoading } = useQuery(
    ['recommend_category', categoryId, bookType, isEbook, user.id],
    () =>
      categoryClient.getRecommendationBooks({
        categoryId,
        bookId,
        bookType,
        isEbook,
        limitPerPage,
      }),
    {
      enabled: canFetchApi,
    }
  )

  const { mutate: toggleBookLike, isLoading: isLoadingToggleBookLike } =
    useMutation((id: number) => bookClient.toggleBookLike(id), {
      onSuccess: async (res, id) => {
        queryClient.setQueryData<RecommendCatagoryType[] | undefined>(
          ['recommend_category', categoryId, bookType, isEbook, user.id],
          oldData => {
            if (oldData) {
              const temp = cloneDeep(oldData)
              const index = temp.findIndex(row => row.id === id)
              if (index !== -1) {
                temp[index].isLiked = res === LikeSuccessStatusEnum.CREATE
                temp[index].likeCount =
                  res === LikeSuccessStatusEnum.CREATE
                    ? temp[index].likeCount + 1
                    : Math.max(0, temp[index].likeCount - 1)
                return temp
              }

              return oldData
            }

            return oldData
          }
        )
      },
    })

  const handleLikeBook = useCallback(
    async (id: number) => {
      if (isAuthenticated) {
        await toggleBookLike(id)
      } else {
        loginModal.show()
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isAuthenticated]
  )

  return {
    data: recommends,
    isLoading: isRecommendLoading,
    isLoadingToggleBookLike,
    handleLikeBook,
  }
}
